import {
  deleteReceiptRemote,
  fetchAccpayPreviewRemote,
  fetchReceipt,
  fetchReceiptCategories,
  fetchReceiptTotals,
  fetchSupplierContactRemote,
  patchReceiptRemote,
  setReceiptArchivedRemote,
  type ReceiptCategoryOption,
  type ReceiptCategoryTotal,
  type ReceiptTotals,
} from '../api/client.js';
import {
  captureReceiptLocal,
  deleteReceiptLocal,
  getReceiptLocal,
  listReceiptsLocal,
  PHOTO_LOST_REASON,
  putReceiptLocal,
  receiptPhotoLost,
  retakeReceiptLocal,
  type LocalReceipt,
} from '../db/receiptsDb.js';
import {
  formatLastUpdated,
  getSyncFreshness,
  hasMergeStale,
} from '../sync/freshness.js';
import { prepareReceiptUploadFile, ReceiptTooLargeError, ReceiptUnsupportedTypeError } from '../receipts/prepareUpload.js';
import { confirmBlockers } from '../receipts/confirm.js';
import { suggestMoneyFixes, reconcileMoney } from '../../../src/receipts/money.js';
import {
  COMMON_CURRENCIES,
  formatMinor,
  formatReceiptListAmount,
} from '../../../src/receipts/currency.js';
import {
  RECEIPT_CATEGORIES,
  RECEIPT_CATEGORY_ACCOUNTANT_CONFIRM,
  RECEIPT_CATEGORY_LABEL,
  RECEIPT_CATEGORY_VAT_RECOVERY_BLOCKED,
} from '../../../src/receipts/categories.js';
import {
  TaxDerivationError,
  deriveReceiptTaxType,
} from '../../../src/receipts/taxFromReceipt.js';
import { vatEvidenceState } from '../../../src/receipts/vatEvidence.js';
import { flushPendingJobs, requeueFailed } from '../sync/flush.js';
import {
  listEmptyHtml,
  listErrorHtml,
  listSkeletonHtml,
} from './listStates.js';
import {
  bindRechargeSection,
  loadRechargeLists,
  rechargeSectionHtml,
} from './receiptRecharge.js';
import {
  bindSupplierContactSection,
  failedSupplierContact,
  supplierContactSectionHtml,
} from './receiptSupplierContact.js';
import {
  bindReceiptHoldSection,
  bindXeroPushSection,
  receiptBillReadbackHtml,
  receiptHoldSectionHtml,
  xeroPushSectionHtml,
} from './receiptXeroPush.js';
import { isWideLayout, subscribeWideLayout } from './wideLayout.js';
import {
  MONEY_OVERRIDE_BLOCK,
  PUSH_DISPLAY_LABEL,
  receiptPushDisplayState,
} from '../../../src/receipts/enqueueAccpay.js';
import {
  decideReceiptDateConfirm,
  differingBillDatesLabel,
  londonToday,
  normalizePrintedWeekday,
} from '../../../src/receipts/receiptDate.js';
import {
  RECEIPT_GROUP_LABEL,
  RECEIPT_GROUPS,
  receiptGroup,
  receiptNeedsMe,
  unrechargedSterlingPence,
  xeroMismatchLabels,
  type ReceiptGroupId,
} from '../../../src/receipts/receiptGroups.js';
import {
  MANUAL_RESOLVE_REASON_LABEL,
  MANUAL_RESOLVE_REASONS,
  manualResolveLabel,
  manualResolveWarnings,
  parseManualResolve,
} from '../../../src/receipts/manualResolve.js';

function fallbackCategoryOptions(): ReceiptCategoryOption[] {
  return RECEIPT_CATEGORIES.map((id) => ({
    id,
    label: RECEIPT_CATEGORY_LABEL[id],
    vat_recovery_blocked: RECEIPT_CATEGORY_VAT_RECOVERY_BLOCKED[id],
    accountant_confirm_needed: RECEIPT_CATEGORY_ACCOUNTANT_CONFIRM[id],
  }));
}

const STATUS_LABEL: Record<string, string> = {
  pending_upload: 'Waiting to upload',
  pending_extraction: 'Extracting',
  awaiting_review: 'Needs review',
  confirmed: 'Confirmed',
  sent_to_accounts: 'Sent to accounts',
};

function receiptDateDecision(
  item: LocalReceipt,
  receiptDate: string | null,
  dateEdited: boolean,
  acceptUnsettled: boolean,
) {
  if (!receiptDate) return null;
  const captured = item.captured_at.slice(0, 10);
  return decideReceiptDateConfirm({
    receiptDate,
    capturedOn: /^\d{4}-\d{2}-\d{2}$/.test(captured) ? captured : null,
    today: londonToday(),
    printedWeekday: item.receipt_date_facts_known
      ? normalizePrintedWeekday(item.receipt_date_weekday)
      : null,
    monthNamed: item.receipt_date_facts_known
      ? (item.receipt_date_month_named ?? null)
      : null,
    factsKnown: Boolean(item.receipt_date_facts_known),
    dateEdited,
    acceptUnsettled,
  });
}

function dateUnsettledHtml(item: LocalReceipt, canEdit: boolean): string {
  if (!canEdit || !item.receipt_date) return '';
  const decision = receiptDateDecision(
    item,
    item.receipt_date.slice(0, 10),
    false,
    false,
  );
  if (!decision || decision.ok) return '';
  if (decision.code === 'receipt_date_unsettled') {
    return `<p class="hint">${escapeHtml(decision.message)}</p>
      <label class="receipt-override-ack">
        <input type="checkbox" data-date-accept />
        <span>I checked this date on the receipt</span>
      </label>`;
  }
  return `<p class="hint">${escapeHtml(decision.message)}</p>`;
}

function receiptListStatus(r: LocalReceipt): string {
  if (r.status !== 'confirmed' && r.status !== 'sent_to_accounts') {
    return STATUS_LABEL[r.status] ?? r.status;
  }
  return PUSH_DISPLAY_LABEL[
    receiptPushDisplayState({
      status: r.status,
      xero_bill_id: r.xero_bill_id,
      xero_push_outcome: r.xero_push_outcome,
      held: r.held,
      money_reconcile_overridden: r.money_reconcile_overridden,
    })
  ];
}

const EXTRACTION_ERROR_LABEL: Record<string, string> = {
  not_configured: 'Extraction not configured — enter fields manually.',
  invalid_response: 'Couldn’t read structured fields — enter manually.',
  timeout: 'Extraction timed out — enter manually.',
  rate_limited: 'Extraction rate-limited — enter manually.',
  api_error: 'Extraction failed — enter manually.',
  image_unreadable: 'Image couldn’t be read — enter manually.',
  max_attempts: 'Extraction retries exhausted — enter manually.',
};

function fieldLow(
  item: LocalReceipt,
  field: string,
): boolean {
  return (item.low_confidence_fields ?? []).includes(field);
}

function fieldLabelHtml(label: string, item: LocalReceipt, field: string): string {
  const low = fieldLow(item, field);
  return `<span>${escapeHtml(label)}${
    low ? ' <em class="receipt-low-flag">Check</em>' : ''
  }</span>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatPence(p: number | null | undefined): string {
  return formatMinor('GBP', p);
}

function formatAmount(
  currency: string | null | undefined,
  p: number | null | undefined,
): string {
  return formatMinor(currency, p);
}

function currencyOptionsHtml(selected: string | null | undefined): string {
  const codes = new Set<string>(COMMON_CURRENCIES);
  if (selected) codes.add(selected.toUpperCase());
  return [...codes]
    .map(
      (c) =>
        `<option value="${c}" ${selected === c ? 'selected' : ''}>${c}</option>`,
    )
    .join('');
}

function gbpPreviewHtml(item: LocalReceipt): string {
  if (!item.currency) {
    return `<p class="hint">Currency is read from the invoice — pick it before confirming. Never assumed GBP.</p>`;
  }
  if (item.currency === 'GBP') {
    return `<p class="hint">Invoice is in GBP. Xero will be posted at these figures.</p>`;
  }
  if (item.gbp_gross_pence == null && item.gbp_net_pence == null) {
    return `<p class="hint">Original ${escapeHtml(item.currency)} ${formatAmount(item.currency, item.gross_pence)}. GBP estimate is fetched when you save or confirm (HMRC period rate).</p>`;
  }
  const rate =
    item.fx_rate != null
      ? `1 ${escapeHtml(item.currency)} = ${Number(item.fx_rate).toFixed(4)} GBP`
      : '';
  let basis = 'Rate';
  if (item.fx_source === 'hmrc_period' && item.fx_period) {
    basis = `HMRC period rate ${escapeHtml(item.fx_period)}`;
  } else if (item.fx_source === 'ecb_frankfurter') {
    const asOf = item.fx_rate_as_of
      ? ` as of ${escapeHtml(item.fx_rate_as_of)}`
      : '';
    basis = `ECB spot${asOf}`;
  } else if (item.fx_source === 'xero_total') {
    basis = 'Xero total';
  }
  const rateBit = rate ? ` · ${rate}` : '';
  return `<div class="receipt-fx-preview" role="status">
    <p><strong>As issued</strong> ${formatAmount(item.currency, item.net_pence)} net · ${formatAmount(item.currency, item.vat_pence)} VAT · ${formatAmount(item.currency, item.gross_pence)} gross</p>
    <p><strong>Approximate GBP</strong> ${formatPence(item.gbp_net_pence)} net · ${formatPence(item.gbp_vat_pence)} VAT · ${formatPence(item.gbp_gross_pence)} gross</p>
    <p class="hint">${basis}${rateBit}. Bank rec matches the card; Xero holds the difference.</p>
  </div>`;
}

function poundsToPence(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t.replace(/£/g, '').replace(/,/g, ''));
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function penceToInput(p: number | null): string {
  if (p == null) return '';
  return (p / 100).toFixed(2);
}

function dateInputValue(value: string | null | undefined): string {
  if (!value) return '';
  return value.slice(0, 10);
}

const LIBRARY_ACCEPT = 'image/*,application/pdf,.pdf';

function isPdfMime(mime: string | null | undefined): boolean {
  return (mime ?? '').toLowerCase() === 'application/pdf';
}

function thumbSrc(item: LocalReceipt): string {
  const uploaded = item.syncState === 'synced' || item.status !== 'pending_upload';
  if (item.localPreviewUrl && !isPdfMime(item.mime_type)) {
    return item.localPreviewUrl;
  }
  if (uploaded) {
    return `/receipts/${item.id}/image?variant=thumb`;
  }
  return '';
}

function receiptThumbInner(item: LocalReceipt, alt = ''): string {
  const src = thumbSrc(item);
  if (src) {
    return `<img src="${escapeHtml(src)}" alt="${escapeHtml(alt)}" loading="lazy" />`;
  }
  if (isPdfMime(item.mime_type)) {
    return `<span class="receipt-pdf-badge">PDF</span>`;
  }
  return '';
}

function receiptPreviewInner(item: LocalReceipt): string {
  const src = thumbSrc(item);
  if (src) return `<img src="${escapeHtml(src)}" alt="Receipt" />`;
  if (isPdfMime(item.mime_type)) {
    return `<div class="receipt-pdf-placeholder"><span class="receipt-pdf-badge">PDF</span><p class="muted">PDF invoice — page preview appears after upload</p></div>`;
  }
  return `<p class="muted">No preview</p>`;
}

function categoryLabel(
  code: string,
  options: ReceiptCategoryOption[] = fallbackCategoryOptions(),
): string {
  return options.find((c) => c.id === code)?.label ?? code;
}

function vatFieldsFromRemote(remote: {
  supplier_vat_number?: string | null;
  vat_evidence_state?: string | null;
}): {
  supplier_vat_number: string | null;
  vat_evidence_state: string | null;
} {
  return {
    supplier_vat_number: remote.supplier_vat_number ?? null,
    vat_evidence_state: remote.vat_evidence_state ?? null,
  };
}

function localVatEvidence(
  cur: LocalReceipt,
  fields: { vat_pence: number | null; supplier_vat_number: string | null },
): string {
  return vatEvidenceState({
    vat_pence: fields.vat_pence,
    supplier_vat_number: fields.supplier_vat_number,
    vatNumberRead:
      cur.vat_evidence_state === 'defective' ||
      cur.vat_evidence_state === 'evidenced' ||
      Boolean(fields.supplier_vat_number?.trim()) ||
      (cur.fields_edited ?? []).includes('supplier_vat_number'),
  });
}

function receiptPushBannerHtml(item: LocalReceipt): string {
  if (item.status !== 'confirmed' && item.status !== 'sent_to_accounts') {
    return '';
  }
  const display = receiptPushDisplayState({
    status: item.status,
    xero_bill_id: item.xero_bill_id,
    xero_push_outcome: item.xero_push_outcome,
    held: item.held,
    money_reconcile_overridden: item.money_reconcile_overridden,
  });
  if (display === 'queued') {
    return `<div class="receipt-ok-banner" role="status"><p>Queued for Xero — will send when you’re connected.</p></div>`;
  }
  if (display === 'action_needed' && !item.held) {
    const reason =
      item.xero_push_error?.trim() ||
      'Needs you before this can go to Xero.';
    return `<div class="receipt-action-banner" role="status"><p>${escapeHtml(reason)}</p></div>`;
  }
  if (display === 'unknown') {
    return `<div class="receipt-action-banner" role="status"><p>${escapeHtml(
      item.xero_push_error?.trim() ||
        'Xero outcome unknown — check the bill before creating another.',
    )}</p></div>`;
  }
  if (display === 'failed') {
    return `<div class="receipt-fail-banner" role="status"><p>${escapeHtml(
      item.xero_push_error?.trim() || 'Xero send failed.',
    )}</p></div>`;
  }
  if (display === 'sent_to_accounts') {
    return `<div class="receipt-ok-banner" role="status"><p>Sent to Xero. This receipt is locked — changing a field here would not update the bill. If the account is wrong, edit the bill in Xero.</p></div>`;
  }
  if (display === 'confirmed') {
    return `<div class="receipt-ok-banner" role="status"><p>Confirmed.</p></div>`;
  }
  return '';
}

function rowReason(r: LocalReceipt): string | null {
  if (r.held) return r.held_reason?.trim() || 'Held';
  const mismatch = xeroMismatchLabels(r);
  if (mismatch.length > 0 && receiptGroup(r) === 'needs_you') {
    return mismatch.join(' · ');
  }
  const push = r.xero_push_error?.trim();
  if (push) return push;
  if (r.money_reconcile_overridden) return 'Money mismatch was overridden';
  return r.failReason?.trim() || null;
}

function rowSignals(r: LocalReceipt): string[] {
  const signals: string[] = [];
  if (r.vat_evidence_state === 'defective') signals.push('VAT number missing');
  if (
    r.xero_date_mismatch &&
    !differingBillDatesLabel(r.receipt_date, r.xero_bill_date)
  ) {
    signals.push('Xero date differs');
  }
  if (r.xero_total_mismatch) signals.push('Xero total differs');
  if (r.xero_contact_map_mismatch) signals.push('Xero contact differs');
  return signals;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function monthKey(r: LocalReceipt): string {
  const date = (r.receipt_date ?? r.captured_at).slice(0, 7);
  return /^\d{4}-\d{2}$/.test(date) ? date : 'unknown';
}

function monthLabel(key: string): string {
  const [year, month] = key.split('-');
  const name = MONTH_NAMES[Number(month) - 1];
  return name ? `${name} ${year}` : key;
}

function receiptRowHtml(
  r: LocalReceipt,
  options: ReceiptCategoryOption[],
): string {
  const lost = receiptPhotoLost(r);
  const fail = r.failReason || (lost ? PHOTO_LOST_REASON : null);
  const group = receiptGroup(r);
  const reason = rowReason(r);
  const showReason =
    reason != null && (group === 'needs_you' || group === 'ready_to_send');
  const datePair = differingBillDatesLabel(r.receipt_date, r.xero_bill_date);
  const reasonHasPair =
    datePair != null && reason != null && reason.includes(datePair);
  const reasonParts = new Set(reason?.split(' · ') ?? []);
  const signals = rowSignals(r).filter((signal) => !reasonParts.has(signal));
  return `<li>
                      <button type="button" class="receipt-row" data-open="${r.id}">
                        <span class="receipt-thumb">${receiptThumbInner(r)}</span>
                        <span class="receipt-meta">
                          <strong>${escapeHtml(r.supplier_name || 'Unnamed')}</strong>
                          <span class="muted">${escapeHtml(
                            r.receipt_date ?? r.captured_at.slice(0, 10),
                          )} · ${escapeHtml(
                            formatReceiptListAmount({
                              currency: r.currency,
                              gross_pence: r.gross_pence,
                              gbp_gross_pence: r.gbp_gross_pence,
                            }),
                          )}</span>
                          <span class="receipt-status">${escapeHtml(
                            receiptListStatus(r),
                          )}${r.category ? ` · ${categoryLabel(r.category, options)}` : ''}${
                            r.recharge_job_id ? ' · Recharged' : ''
                          }${
                            r.recharge_decision === 'backfill'
                              ? ' · Overhead backfill'
                              : ''
                          }${
                            manualResolveLabel(r.manual_resolve_reason)
                              ? ` · ${escapeHtml(manualResolveLabel(r.manual_resolve_reason) ?? '')}`
                              : ''
                          }${
                            r.xero_bill_status
                              ? ` · ${escapeHtml(r.xero_bill_status)}`
                              : ''
                          }${
                            r.xero_invoice_number
                              ? ` · ${escapeHtml(r.xero_invoice_number)}`
                              : ''
                          }</span>
                          ${
                            signals.length
                              ? `<span class="receipt-signals">${signals
                                  .map(
                                    (s) =>
                                      `<span class="receipt-signal">${escapeHtml(s)}</span>`,
                                  )
                                  .join('')}</span>`
                              : ''
                          }
                          ${
                            showReason
                              ? `<span class="receipt-row-reason">${escapeHtml(reason)}</span>`
                              : ''
                          }
                          ${
                            datePair && !(showReason && reasonHasPair)
                              ? `<span class="receipt-row-reason">${escapeHtml(datePair)}</span>`
                              : ''
                          }
                          ${
                            fail && fail !== reason
                              ? `<span class="receipt-fail">${escapeHtml(fail)}</span>`
                              : ''
                          }
                        </span>
                      </button>
                    </li>`;
}

function groupsHtml(
  rows: LocalReceipt[],
  options: ReceiptCategoryOption[],
  resolvedFilter: 'all' | 'by_me',
  resolvedStayOpen: boolean,
): string {
  const byGroup = new Map<ReceiptGroupId, LocalReceipt[]>();
  for (const id of RECEIPT_GROUPS) byGroup.set(id, []);
  for (const row of rows) {
    const bucket = byGroup.get(receiptGroup(row));
    if (bucket) bucket.push(row);
  }
  const sections: string[] = [];
  for (const id of RECEIPT_GROUPS) {
    const items = byGroup.get(id) ?? [];
    if (items.length === 0) continue;
    items.sort((a, b) =>
      (b.receipt_date ?? b.captured_at).localeCompare(
        a.receipt_date ?? a.captured_at,
      ),
    );
    const open = id === 'resolved' ? (resolvedStayOpen ? ' open' : '') : ' open';
    const head =
      id === 'unrecharged'
        ? `<span>${RECEIPT_GROUP_LABEL[id]}</span><strong class="receipt-group-total">${escapeHtml(
            formatPence(unrechargedSterlingPence(items)),
          )}</strong>`
        : `<span>${RECEIPT_GROUP_LABEL[id]}</span><span class="muted">${items.length}</span>`;
    const body =
      id === 'resolved'
        ? resolvedMonthsHtml(items, options, resolvedFilter)
        : listHtml(items, options);
    sections.push(
      `<details class="receipt-group" data-group="${id}"${open}>
        <summary class="receipt-group-summary">${head}</summary>
        ${body}
      </details>`,
    );
  }
  return sections.join('');
}

function listHtml(rows: LocalReceipt[], options: ReceiptCategoryOption[]): string {
  return `<ul class="receipt-list">${rows
    .map((r) => receiptRowHtml(r, options))
    .join('')}</ul>`;
}

function resolvedMonthsHtml(
  rows: LocalReceipt[],
  options: ReceiptCategoryOption[],
  resolvedFilter: 'all' | 'by_me',
): string {
  const byMe = rows.filter((row) => Boolean(row.manual_resolved_at)).length;
  const shown =
    resolvedFilter === 'by_me'
      ? rows.filter((row) => Boolean(row.manual_resolved_at))
      : rows;
  const filter = `<div class="receipt-resolve-filter" role="group" aria-label="Resolved rows">
    <button type="button" class="btn-secondary" data-resolved-filter="all" aria-pressed="${resolvedFilter === 'all' ? 'true' : 'false'}">All ${rows.length}</button>
    <button type="button" class="btn-secondary" data-resolved-filter="by_me" aria-pressed="${resolvedFilter === 'by_me' ? 'true' : 'false'}">By me ${byMe}</button>
  </div>`;
  if (shown.length === 0) {
    return `${filter}<p class="hint receipt-resolve-empty">Nothing resolved manually.</p>`;
  }
  const months = new Map<string, LocalReceipt[]>();
  for (const row of shown) {
    const key = monthKey(row);
    const list = months.get(key) ?? [];
    list.push(row);
    months.set(key, list);
  }
  const keys = [...months.keys()].sort((a, b) => b.localeCompare(a));
  return (
    filter +
    keys
      .map((key) => {
        const items = months.get(key) ?? [];
        return `<details class="receipt-month">
        <summary class="receipt-group-summary"><span>${escapeHtml(monthLabel(key))}</span><span class="muted">${items.length}</span></summary>
        ${listHtml(items, options)}
      </details>`;
      })
      .join('')
  );
}

function paintNeedsMeBadge(count: number): void {
  const tab = document.querySelector<HTMLButtonElement>('[data-tab="receipts"]');
  if (!tab) return;
  let badge = tab.querySelector<HTMLElement>('.receipt-needs-badge');
  if (count <= 0) {
    badge?.remove();
    return;
  }
  if (!badge) {
    badge = document.createElement('span');
    badge.className = 'receipt-needs-badge';
    tab.append(badge);
  }
  badge.textContent = count > 9 ? '9+' : String(count);
}

function taxHintHtml(
  item: {
    category: string | null;
    currency?: string | null;
    net_pence: number | null;
    vat_pence: number | null;
  },
  options: ReceiptCategoryOption[],
): string {
  if (!item.category) return '';
  const meta = options.find((c) => c.id === item.category);
  const blocked = Boolean(meta?.vat_recovery_blocked);
  try {
    const derived = deriveReceiptTaxType({
      net_pence: item.net_pence,
      vat_pence: item.vat_pence,
      vatRecoveryBlocked: blocked,
      currency: item.currency,
      category: item.category,
    });
    const accountant = meta?.accountant_confirm_needed
      ? ' Flagged for your accountant to confirm — conservative (under-reclaim) rather than 429/INPUT2.'
      : '';
    return `<p class="hint receipt-tax-hint">${escapeHtml(derived.note)}${escapeHtml(accountant)}</p>`;
  } catch (err) {
    if (err instanceof TaxDerivationError) {
      return `<p class="hint receipt-tax-hint">${escapeHtml(err.message)}</p>`;
    }
    return '';
  }
}

function categoryMixHtml(
  rows: ReceiptCategoryTotal[] | undefined,
  title: string,
  sub: string,
): string {
  if (!rows || rows.length === 0) {
    return `<section class="receipt-spend">
      <h2 class="money-subhead">${escapeHtml(title)}</h2>
      <p class="hint">${escapeHtml(sub)}</p>
      <p class="muted">No confirmed spend in this period yet. Confirm a receipt to see it here.</p>
    </section>`;
  }
  const total = rows.reduce((s, r) => s + (Number(r.gross_pence) || 0), 0);
  const fuel = rows.find((r) => r.category === 'fuel');
  return `<section class="receipt-spend">
    <h2 class="money-subhead">${escapeHtml(title)}</h2>
    <p class="hint">${escapeHtml(sub)}</p>
    ${
      fuel
        ? `<p class="receipt-fuel-callout">Fuel <strong>${formatPence(Number(fuel.gross_pence))}</strong> · ${fuel.count} receipt${fuel.count === 1 ? '' : 's'}</p>`
        : ''
    }
    <div class="mix-bars">
      ${rows
        .map((r, i) => {
          const pence = Number(r.gross_pence) || 0;
          const pct =
            total > 0 ? Math.round((pence / total) * 1000) / 10 : 0;
          return `<div class="mix-row${r.category === 'fuel' ? ' is-fuel' : ''}">
            <span class="mix-label">${escapeHtml(categoryLabel(r.category))}</span>
            <span class="mix-track"><span class="mix-fill chart-s${(i % 5) + 1}" style="width:${Math.min(100, pct)}%"></span></span>
            <span class="mix-pct">${pct}%</span>
            <span class="muted mix-val">${formatPence(pence)}</span>
          </div>`;
        })
        .join('')}
    </div>
  </section>`;
}

function manualResolveHtml(item: LocalReceipt): string {
  if (item.manual_resolved_at) {
    const label =
      manualResolveLabel(item.manual_resolve_reason) ?? 'Resolved by me';
    const waiting = item.manual_resolve_pending
      ? `<p class="hint">Saved on this phone. It will reach the server when you’re online.</p>`
      : '';
    return `<section class="receipt-resolve">
      <p class="receipt-row-reason">${escapeHtml(label)}</p>
      ${
        item.manual_resolve_note
          ? `<p>${escapeHtml(item.manual_resolve_note)}</p>`
          : ''
      }
      ${waiting}
      <button type="button" class="btn-secondary btn-block" data-unresolve>Unresolve</button>
      <p class="hint">Puts this row back in the group the rules give it. The hold, the flags, and the bill stay as they are.</p>
    </section>`;
  }
  const warnings = manualResolveWarnings(item);
  const chips = MANUAL_RESOLVE_REASONS.map(
    (reason) =>
      `<button type="button" class="btn-secondary" data-resolve-reason="${reason}" aria-pressed="false">${escapeHtml(MANUAL_RESOLVE_REASON_LABEL[reason])}</button>`,
  ).join('');
  return `<section class="receipt-resolve">
    <button type="button" class="btn-secondary btn-block" data-resolve-open>Resolve manually</button>
    <div class="sheet receipt-resolve-sheet" data-resolve-sheet hidden>
      <div class="sheet-backdrop" data-resolve-cancel></div>
      <div class="sheet-panel" role="dialog" aria-modal="true" aria-labelledby="resolve-title">
        <div class="sheet-head">
          <h2 id="resolve-title">Resolve manually</h2>
          <button type="button" class="btn-secondary" data-resolve-cancel>Close</button>
        </div>
        ${warnings
          .map(
            (line) =>
              `<p class="receipt-resolve-warn">${escapeHtml(line)}</p>`,
          )
          .join('')}
        <div class="receipt-resolve-reasons" role="group" aria-label="Reason">
          ${chips}
        </div>
        <label class="field"><span>Note</span>
          <textarea data-resolve-note maxlength="500" rows="2" placeholder="Optional, except for Other"></textarea>
        </label>
        <p class="hint" data-resolve-note-hint hidden>Other needs a note.</p>
        <button type="button" class="submit" data-resolve-confirm disabled>Resolve</button>
      </div>
    </div>
  </section>`;
}

async function queueManualResolve(
  id: string,
  reason: string,
  note: string,
): Promise<void> {
  const parsed = parseManualResolve({ reason, note });
  if (!parsed.ok) throw new Error(parsed.message);
  const cur = await getReceiptLocal(id);
  if (!cur) throw new Error('Receipt isn’t on this device.');
  await putReceiptLocal({
    ...cur,
    manual_resolved_at: cur.manual_resolved_at ?? new Date().toISOString(),
    manual_resolve_reason: parsed.reason,
    manual_resolve_note: parsed.note,
    manual_resolve_pending: 'set',
    updatedAt: new Date().toISOString(),
  });
  void flushPendingJobs();
}

async function queueManualUnresolve(id: string): Promise<void> {
  const cur = await getReceiptLocal(id);
  if (!cur) throw new Error('Receipt isn’t on this device.');
  await putReceiptLocal({
    ...cur,
    manual_resolved_at: null,
    manual_resolve_reason: null,
    manual_resolve_note: null,
    manual_resolve_pending: 'clear',
    updatedAt: new Date().toISOString(),
  });
  void flushPendingJobs();
}

function bindManualResolve(
  root: HTMLElement,
  id: string,
  done: () => Promise<void>,
  onError: (text: string) => void,
): void {
  const sheet = root.querySelector<HTMLElement>('[data-resolve-sheet]');
  const open = () => {
    if (sheet) sheet.hidden = false;
  };
  const close = () => {
    if (sheet) sheet.hidden = true;
  };
  root.querySelector('[data-resolve-open]')?.addEventListener('click', open);
  root.querySelectorAll('[data-resolve-cancel]').forEach((el) => {
    el.addEventListener('click', close);
  });
  const note = root.querySelector<HTMLTextAreaElement>('[data-resolve-note]');
  const confirm = root.querySelector<HTMLButtonElement>('[data-resolve-confirm]');
  const hint = root.querySelector<HTMLElement>('[data-resolve-note-hint]');
  const selected = (): string | null =>
    root.querySelector<HTMLButtonElement>('[data-resolve-reason][aria-pressed="true"]')
      ?.dataset.resolveReason ?? null;
  const refreshConfirm = () => {
    const reason = selected();
    const text = note?.value ?? '';
    const otherBlocked = reason === 'other' && text.trim().length === 0;
    if (hint) hint.hidden = reason !== 'other' || !otherBlocked;
    if (confirm) confirm.disabled = !reason || otherBlocked;
  };
  root.querySelectorAll<HTMLButtonElement>('[data-resolve-reason]').forEach((btn) => {
    btn.addEventListener('click', () => {
      root.querySelectorAll<HTMLButtonElement>('[data-resolve-reason]').forEach((other) => {
        other.setAttribute('aria-pressed', other === btn ? 'true' : 'false');
      });
      refreshConfirm();
    });
  });
  note?.addEventListener('input', refreshConfirm);
  confirm?.addEventListener('click', () => {
    const reason = selected();
    if (!reason || !confirm) return;
    confirm.disabled = true;
    void (async () => {
      try {
        await queueManualResolve(id, reason, note?.value ?? '');
        await done();
      } catch (err) {
        confirm.disabled = false;
        onError(
          err instanceof Error ? err.message : 'Couldn’t resolve this receipt.',
        );
        await done();
      }
    })();
  });
  root.querySelector('[data-unresolve]')?.addEventListener('click', () => {
    void (async () => {
      try {
        await queueManualUnresolve(id);
        await done();
      } catch (err) {
        onError(
          err instanceof Error ? err.message : 'Couldn’t put this row back.',
        );
        await done();
      }
    })();
  });
}

export type ReceiptsController = {
  refresh: () => Promise<void>;
  /** Open the camera (or library) picker from the Receipts tab. */
  openCapture: (mode?: 'camera' | 'library') => void;
  open: (id: string) => Promise<void>;
};

export async function mountReceipts(
  root: HTMLElement,
  opts: {
    onChange?: () => void;
    onOpenJob?: (id: string) => void;
  } = {},
): Promise<ReceiptsController> {
  type View = 'list' | 'detail';
  let view: View = 'list';
  let detailId: string | null = null;
  let filterQ = '';
  let filterCategory = '';
  let showArchived = false;
  let resolvedFilter: 'all' | 'by_me' = 'all';
  let resolvedStayOpen = false;
  let totals: ReceiptTotals | null = null;
  /** Prevents refreshAll from wiping a just-opened capture detail. */
  let capturing = false;
  let captureNotice: string | null = null;
  let extractPollTimer: ReturnType<typeof setTimeout> | null = null;
  let detailFeedback: { kind: 'ok' | 'error'; text: string; lines?: string[] } | null =
    null;
  let blockedFieldNames: string[] = [];
  /** Extra Confirm acknowledgement for a failed net+VAT vs gross check. */
  let moneyOverrideAck = false;
  let categoryOptions: ReceiptCategoryOption[] = fallbackCategoryOptions();
  let xeroPushPhase: 'idle' | 'confirming' | 'sending' = 'idle';
  let xeroPushReceiptId: string | null = null;

  /** When set, the next paste event on the receipts panel is treated as a capture. */
  let pasteArmed = false;

  function ensureShell() {
    if (root.querySelector(':scope > .split-shell')) return;
    root.innerHTML = `
      <div class="split-shell">
        <div class="split-head"></div>
        <div class="split-layout">
          <div class="split-list"></div>
          <div class="split-detail"></div>
        </div>
      </div>`;
  }

  function shellEl(): HTMLElement {
    ensureShell();
    return root.querySelector(':scope > .split-shell') as HTMLElement;
  }

  function headEl(): HTMLElement {
    return shellEl().querySelector(':scope > .split-head') as HTMLElement;
  }

  function listEl(): HTMLElement {
    return shellEl().querySelector('.split-list') as HTMLElement;
  }

  function detailEl(): HTMLElement {
    return shellEl().querySelector('.split-detail') as HTMLElement;
  }

  function syncSplitClasses() {
    const wide = isWideLayout();
    root.classList.toggle('is-wide', wide);
    root.classList.toggle('is-phone-detail', !wide && view === 'detail');
  }

  function paintEmptyDetail() {
    if (!isWideLayout() || detailId) return;
    detailEl().innerHTML = `
      <section class="receipts-panel split-detail-empty">
        <p class="muted">Select a receipt</p>
      </section>`;
  }

  function markSelectedRow() {
    shellEl().querySelectorAll('[data-open]').forEach((el) => {
      const id = (el as HTMLElement).dataset.open;
      const on = Boolean(detailId && id === detailId);
      el.classList.toggle('is-selected', on);
      if (on) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });
  }

  function clearExtractPoll() {
    if (extractPollTimer != null) {
      clearTimeout(extractPollTimer);
      extractPollTimer = null;
    }
  }

  function fileFromClipboardData(data: DataTransfer | null): File | null {
    if (!data) return null;
    for (const item of Array.from(data.items ?? [])) {
      if (!item.type.startsWith('image/')) continue;
      const asFile = item.getAsFile();
      if (asFile && asFile.size > 0) {
        const ext = item.type.includes('png')
          ? 'png'
          : item.type.includes('webp')
            ? 'webp'
            : 'jpg';
        return new File([asFile], `screenshot.${ext}`, {
          type: item.type || asFile.type || 'image/png',
          lastModified: Date.now(),
        });
      }
    }
    for (const file of Array.from(data.files ?? [])) {
      if (file.type.startsWith('image/') && file.size > 0) return file;
    }
    return null;
  }

  async function fileFromClipboardRead(): Promise<File | null> {
    const clip = navigator.clipboard;
    if (!clip || typeof clip.read !== 'function') return null;
    const items = await clip.read();
    for (const item of items) {
      const type = item.types.find((t) => t.startsWith('image/'));
      if (!type) continue;
      const blob = await item.getType(type);
      if (!blob || blob.size === 0) continue;
      const ext = type.includes('png')
        ? 'png'
        : type.includes('webp')
          ? 'webp'
          : 'jpg';
      return new File([blob], `screenshot.${ext}`, {
        type,
        lastModified: Date.now(),
      });
    }
    return null;
  }

  async function tryPasteScreenshot() {
    try {
      const fromApi = await fileFromClipboardRead();
      if (fromApi) {
        pasteArmed = false;
        await onFile(fromApi);
        return;
      }
    } catch (err) {
      console.warn('clipboard screenshot read failed; using paste target', err);
    }
    pasteArmed = true;
    captureNotice =
      'Screenshot is on the clipboard — long-press the paste box below and choose Paste (or press ⌘V / Ctrl+V).';
    await renderList({ preserveDetail: isWideLayout() });
    const target = shellEl().querySelector(
      '[data-paste-target]',
    ) as HTMLElement | null;
    target?.focus();
  }

  function onPanelPaste(ev: ClipboardEvent) {
    if (view !== 'list' && !pasteArmed) return;
    const file = fileFromClipboardData(ev.clipboardData);
    if (!file) {
      if (pasteArmed) {
        ev.preventDefault();
        captureNotice =
          'That paste wasn’t an image. Copy a screenshot, then Paste again.';
        void renderList();
      }
      return;
    }
    ev.preventDefault();
    pasteArmed = false;
    void onFile(file);
  }

  root.addEventListener('paste', onPanelPaste);

  async function refresh() {
    if (capturing) return;
    if (isWideLayout()) {
      await renderList({ preserveDetail: Boolean(detailId) });
      if (detailId) await renderDetail(detailId);
      return;
    }
    if (view === 'detail' && detailId) {
      await renderDetail(detailId);
    } else {
      await renderList();
    }
  }

  function openCapture(mode: 'camera' | 'library' = 'camera') {
    const ensure = async () => {
      if (view !== 'list' && !isWideLayout()) {
        await renderList();
      }
      const sel = mode === 'library' ? '[data-library]' : '[data-capture]';
      let input = shellEl().querySelector(sel) as HTMLInputElement | null;
      if (!input && mode === 'library') {
        input = shellEl().querySelector('[data-capture]') as HTMLInputElement | null;
      }
      if (!input) {
        captureNotice =
          'Couldn’t open the camera. Open the Receipts tab and try Take photo.';
        await renderList();
        return;
      }
      input.value = '';
      input.click();
    };
    void ensure();
  }

  async function renderList(opts?: { preserveDetail?: boolean }) {
    const preserveDetail = Boolean(opts?.preserveDetail && detailId);
    if (!preserveDetail) {
      clearExtractPoll();
      view = 'list';
      detailId = null;
      detailFeedback = null;
      blockedFieldNames = [];
      moneyOverrideAck = false;
      xeroPushPhase = 'idle';
      xeroPushReceiptId = null;
    } else if (view !== 'detail') {
      view = 'list';
    }

    ensureShell();
    syncSplitClasses();
    headEl().innerHTML = `
      <section class="receipts-panel">
        <h1 class="screen-title">Receipts</h1>
      </section>
    `;
    listEl().innerHTML = listSkeletonHtml(4, 'list-skel-row skel-receipt');
    if (!preserveDetail) paintEmptyDetail();

    let listError = false;
    let filtered: LocalReceipt[] = [];
    let allCount = 0;
    let needsMeCount = 0;
    let archivedNeedsMe = 0;
    try {
      const all = await listReceiptsLocal();
      allCount = all.length;
      needsMeCount = all.filter((r) => receiptNeedsMe(r)).length;
      archivedNeedsMe = all.filter(
        (r) => receiptNeedsMe(r) && Boolean(r.archived_at),
      ).length;
      const q = filterQ.trim().toLowerCase();
      filtered = all.filter((r) => {
        const archived = Boolean(r.archived_at);
        if (showArchived ? !archived : archived) return false;
        if (filterCategory && r.category !== filterCategory) return false;
        if (!q) return true;
        return (r.supplier_name ?? '').toLowerCase().includes(q);
      });
    } catch (err) {
      console.error('receipts list failed', err);
      listError = true;
    }
    paintNeedsMeBadge(needsMeCount);

    try {
      totals = await fetchReceiptTotals();
    } catch (err) {
      console.warn('receipt totals unavailable; keeping last', err);
    }
    try {
      categoryOptions = await fetchReceiptCategories();
    } catch (err) {
      console.warn('receipt categories unavailable; using built-in list', err);
    }

    const freshness = await getSyncFreshness();
    const staleBanner = hasMergeStale(freshness)
      ? `<p class="stale-banner" role="status">Receipt list may be out of date${
          freshness.receiptsAt
            ? ` · last updated ${escapeHtml(formatLastUpdated(freshness.receiptsAt))}`
            : ''
        }${
          freshness.receiptsError
            ? ` · ${escapeHtml(freshness.receiptsError)}`
            : ''
        }</p>`
      : '';

    const notice = captureNotice
      ? `<p class="receipt-notice" role="status">${escapeHtml(captureNotice)}</p>`
      : '';
    captureNotice = null;

    const listBody = listError
      ? listErrorHtml()
      : filtered.length === 0
        ? listEmptyHtml(
            allCount === 0
              ? 'No receipts yet. Photo, PDF from library, or paste a screenshot — saves offline and uploads when you’re online.'
              : showArchived
                ? 'Nothing is archived.'
                : 'No receipts match these filters.',
          )
        : groupsHtml(filtered, categoryOptions, resolvedFilter, resolvedStayOpen);

    const needsLine = listError
      ? ''
      : `<p class="receipt-needs-line" role="status">Needs you <strong>${needsMeCount}</strong>${
          !showArchived && archivedNeedsMe
            ? ` <span class="muted">· ${archivedNeedsMe} archived</span>`
            : ''
        }</p>`;

    headEl().innerHTML = `
      <section class="receipts-panel">
        <h1 class="screen-title">Receipts</h1>
        ${staleBanner}
        ${notice}

        <div class="receipt-capture" aria-label="Capture receipt">
          <label class="receipt-capture-btn">
            <input type="file" accept="image/*" capture="environment" data-capture hidden />
            <span>Take photo</span>
          </label>
          <label class="receipt-capture-btn secondary">
            <input type="file" accept="${LIBRARY_ACCEPT}" data-library hidden />
            <span>Choose from library</span>
          </label>
          <button type="button" class="receipt-capture-btn secondary" data-paste>
            Paste screenshot
          </button>
          ${
            pasteArmed
              ? `<div
                  class="receipt-paste-target"
                  data-paste-target
                  contenteditable="true"
                  role="textbox"
                  aria-label="Paste screenshot here"
                  inputmode="none"
                >Tap here, then Paste</div>`
              : `<p class="hint receipt-capture-hint">PDF invoices from email, plus photos and screenshots — library or paste. On iPhone, use Browse / Files if the document is not in Photos.</p>`
          }
        </div>

        ${
          totals
            ? `<div class="receipt-totals" aria-label="Totals">
                <div>
                  <span class="muted">This month</span>
                  <strong>${formatPence(totals.month.gross_pence)}</strong>
                  <span class="muted">VAT ${formatPence(totals.month.vat_pence)}</span>
                </div>
                <div>
                  <span class="muted">${escapeHtml(totals.vat_quarter.label)}</span>
                  <strong>${formatPence(totals.vat_quarter.gross_pence)}</strong>
                  <span class="muted">VAT ${formatPence(totals.vat_quarter.vat_pence)}</span>
                </div>
                <div>
                  <span class="muted">Last 12 months</span>
                  <strong>${formatPence(totals.last_12?.gross_pence ?? 0)}</strong>
                  <span class="muted">VAT ${formatPence(totals.last_12?.vat_pence ?? 0)}</span>
                </div>
              </div>`
            : ''
        }

        ${categoryMixHtml(
          totals?.by_category?.last_12,
          'Spend by category',
          'Confirmed receipts over the last 12 months (fuel highlighted).',
        )}
      </section>
    `;

    listEl().innerHTML = `
      <section class="receipts-panel">
        <div class="receipt-history-head">
          <h2 class="money-subhead">${showArchived ? 'Archived' : 'To do'}</h2>
          <p class="hint">${
            showArchived
              ? 'Archived receipts stay held, queued, or blocked. This only changes what the list shows.'
              : 'Needs you is first. Resolved is filed by month.'
          }</p>
        </div>

        <div class="receipt-filters">
          <input type="search" placeholder="Search supplier" data-q value="${escapeHtml(filterQ)}" />
          <select data-category>
            <option value="">All categories</option>
            ${categoryOptions
              .map(
                (c) =>
                  `<option value="${escapeHtml(c.id)}" ${filterCategory === c.id ? 'selected' : ''}>${escapeHtml(c.label)}</option>`,
              )
              .join('')}
          </select>
          <button type="button" class="btn-secondary" data-archive-view ${showArchived ? 'aria-pressed="true"' : 'aria-pressed="false"'}>${showArchived ? 'Back to current' : 'Archived'}</button>
        </div>
        ${needsLine}

        ${listBody}
      </section>
    `;

    if (!preserveDetail) paintEmptyDetail();
    markSelectedRow();
    syncSplitClasses();

    const chrome = shellEl();
    chrome.querySelector('[data-capture]')?.addEventListener('change', (e) => {
      void onFile((e.target as HTMLInputElement).files?.[0]);
    });
    chrome.querySelector('[data-library]')?.addEventListener('change', (e) => {
      void onFile((e.target as HTMLInputElement).files?.[0]);
    });
    chrome.querySelector('[data-paste]')?.addEventListener('click', () => {
      void tryPasteScreenshot();
    });
    chrome.querySelector('[data-paste-target]')?.addEventListener('beforeinput', (ev) => {
      // Keep the paste box from accumulating typed text; images arrive via paste.
      const ie = ev as InputEvent;
      if (ie.inputType !== 'insertFromPaste') ev.preventDefault();
    });
    chrome.querySelector('[data-list-retry]')?.addEventListener('click', () => {
      void renderList({ preserveDetail });
    });
    chrome.querySelector('[data-q]')?.addEventListener('input', (e) => {
      filterQ = (e.target as HTMLInputElement).value;
      void renderList({ preserveDetail: isWideLayout() });
    });
    chrome.querySelector('[data-category]')?.addEventListener('change', (e) => {
      filterCategory = (e.target as HTMLSelectElement).value;
      void renderList({ preserveDetail: isWideLayout() });
    });
    chrome.querySelector('[data-archive-view]')?.addEventListener('click', () => {
      showArchived = !showArchived;
      void renderList({ preserveDetail: isWideLayout() });
    });
    chrome.querySelectorAll('[data-resolved-filter]').forEach((el) => {
      el.addEventListener('click', () => {
        const next = (el as HTMLButtonElement).dataset.resolvedFilter;
        resolvedStayOpen = true;
        resolvedFilter = next === 'by_me' ? 'by_me' : 'all';
        void renderList({ preserveDetail: isWideLayout() });
      });
    });
    chrome.querySelectorAll('[data-open]').forEach((el) => {
      el.addEventListener('click', () => {
        detailId = (el as HTMLElement).dataset.open ?? null;
        if (!detailId) return;
        view = 'detail';
        markSelectedRow();
        syncSplitClasses();
        void renderDetail(detailId);
      });
    });
  }

  async function onFile(file: File | undefined) {
    if (!file || file.size === 0) {
      captureNotice =
        'Nothing was selected. Pick a PDF invoice or a photo, or paste a screenshot.';
      await renderList();
      return;
    }
    capturing = true;
    const id = crypto.randomUUID();
    try {
      const prepared = await prepareReceiptUploadFile(file, {
        mimeType: file.type,
        filename: file.name,
      });
      await captureReceiptLocal({
        id,
        blob: prepared,
        mime_type: prepared.type || 'image/jpeg',
      });
      detailId = id;
      view = 'detail';
      await renderList({ preserveDetail: true });
      await renderDetail(id);
    } catch (err) {
      console.error('receipt capture failed', err);
      captureNotice =
        err instanceof ReceiptTooLargeError ||
        err instanceof ReceiptUnsupportedTypeError
          ? err.message
          : err instanceof Error
            ? `Couldn’t save that file: ${err.message}`
            : 'Couldn’t save that file. Try again.';
      view = 'list';
      detailId = null;
      await renderList();
      return;
    } finally {
      capturing = false;
    }
    // Sync after detail is on screen so refreshAll cannot wipe the new receipt.
    opts.onChange?.();
  }

  async function renderDetail(id: string) {
    clearExtractPoll();
    view = 'detail';
    detailId = id;
    const item = await getReceiptLocal(id);
    if (!item) {
      view = 'list';
      await renderList();
      return;
    }
    const root = detailEl();
    syncSplitClasses();
    if (xeroPushReceiptId !== id) {
      xeroPushPhase = 'idle';
      xeroPushReceiptId = id;
    }
    const [rechargeLists, contactPreview, accpayPreview] = await Promise.all([
      loadRechargeLists(),
      item.status === 'confirmed' || item.status === 'sent_to_accounts'
        ? fetchSupplierContactRemote(id).catch((err) =>
            failedSupplierContact(
              item.supplier_name ?? '',
              err instanceof Error
                ? err.message
                : 'Could not load Xero contacts.',
            ),
          )
        : Promise.resolve(null),
      item.status === 'confirmed' || item.status === 'sent_to_accounts'
        ? fetchAccpayPreviewRemote(id).catch(() => null)
        : Promise.resolve(null),
    ]);
    const canEdit =
      item.status !== 'sent_to_accounts' &&
      item.status !== 'pending_extraction';
    const photoLost = receiptPhotoLost(item);
    const failText =
      item.failReason || (photoLost ? PHOTO_LOST_REASON : null);
    const moneyFixes =
      item.money_reconcile_ok === false
        ? suggestMoneyFixes({
            net_pence: item.net_pence,
            vat_pence: item.vat_pence,
            gross_pence: item.gross_pence,
          })
        : [];
    const moneyMismatch = item.money_reconcile_ok === false;
    const extracting = item.status === 'pending_extraction';
    const lowCount = (item.low_confidence_fields ?? []).length;
    const manualReason = item.extraction_error
      ? EXTRACTION_ERROR_LABEL[item.extraction_error] ??
        'Extraction unavailable — enter manually.'
      : null;

    root.innerHTML = `
      <section class="receipts-panel">
        <button type="button" class="btn-back" data-back>‹ Receipts</button>
        <h1 class="screen-title">${escapeHtml(item.supplier_name || 'Receipt')}</h1>
        <div class="receipt-preview">
          ${receiptPreviewInner(item)}
        </div>
        <p class="muted receipt-status-line">${escapeHtml(
          receiptListStatus(item),
        )}${
          item.syncState === 'pending' || item.syncState === 'syncing'
            ? ' · uploading…'
            : item.syncState === 'failed'
              ? ' · upload failed'
              : extracting
                ? ' · reading receipt…'
                : ''
        }${
          item.extraction_cost_usd != null
            ? ` · extract ~$${item.extraction_cost_usd.toFixed(4)}`
            : ''
        }</p>
        ${
          extracting
            ? `<p class="receipt-extracting" role="status">Extracting fields from the document. Confirm is unavailable until that finishes — this screen updates when it’s ready.</p>`
            : ''
        }
        ${receiptPushBannerHtml(item)}
        ${
          detailFeedback
            ? `<div class="${detailFeedback.kind === 'error' ? 'receipt-fail-banner' : 'receipt-ok-banner'}" role="alert" data-receipt-feedback>
                <p>${escapeHtml(detailFeedback.text)}</p>
                ${
                  detailFeedback.lines?.length
                    ? `<ul class="receipt-block-list">${detailFeedback.lines
                        .map((l) => `<li>${escapeHtml(l)}</li>`)
                        .join('')}</ul>`
                    : ''
                }
              </div>`
            : ''
        }
        ${
          manualReason
            ? `<div class="receipt-fail-banner" role="status"><p>${escapeHtml(manualReason)}</p></div>`
            : ''
        }
        ${
          moneyMismatch
            ? `<div class="receipt-reconcile-banner" role="alert">
                <p>${escapeHtml(
                  item.money_reconcile_note ||
                    'Net + VAT does not match gross.',
                )}</p>
                ${
                  item.money_reconcile_overridden
                    ? `<p class="receipt-override-flag">Reconciliation was overridden when this was confirmed. The mismatch is on the record.</p>`
                    : ''
                }
                ${
                  moneyFixes.length && canEdit
                    ? `<p class="hint">Pick the split that matches the receipt — one tap, no mental arithmetic.</p>
                       <div class="receipt-money-fixes">
                         ${moneyFixes
                           .map(
                             (f) =>
                               `<button type="button" class="btn-secondary receipt-money-fix" data-money-fix="${escapeHtml(f.id)}" data-net="${f.net_pence}" data-vat="${f.vat_pence}" data-gross="${f.gross_pence}">${escapeHtml(f.label)}</button>`,
                           )
                           .join('')}
                       </div>`
                    : ''
                }
                ${
                  canEdit && item.status !== 'confirmed'
                    ? `<label class="receipt-override-ack">
                         <input type="checkbox" data-money-override ${
                           moneyOverrideAck ? 'checked' : ''
                         } />
                         <span>I have checked the receipt and these figures are what it shows — confirm anyway</span>
                       </label>`
                    : ''
                }
              </div>`
            : ''
        }
        ${
          !item.currency && !extracting && item.status !== 'sent_to_accounts'
            ? `<div class="receipt-reconcile-banner" role="alert">
                <p>Currency was not read from the receipt. Pick it before confirming — it is never assumed GBP.</p>
              </div>`
            : ''
        }
        ${
          lowCount > 0
            ? `<p class="receipt-low-summary" role="status">${lowCount} field${lowCount === 1 ? '' : 's'} marked low confidence — check the highlighted rows first.</p>`
            : ''
        }
        ${
          failText || photoLost
            ? `<div class="receipt-fail-banner" role="alert">
                <p>${escapeHtml(failText ?? PHOTO_LOST_REASON)}</p>
                ${
                  photoLost
                    ? `<label class="btn-primary receipt-retake">
                         <input type="file" accept="image/*" capture="environment" data-retake hidden />
                         <span>Re-take photo</span>
                       </label>
                       <label class="btn-secondary receipt-retake">
                         <input type="file" accept="${LIBRARY_ACCEPT}" data-retake-library hidden />
                         <span>Choose from library</span>
                       </label>`
                    : item.blob
                      ? `<button type="button" class="btn-primary" data-retry-upload>Retry upload</button>`
                      : ''
                }
              </div>`
            : ''
        }
        ${
          item.vat_evidence_state === 'defective'
            ? `<div class="receipt-vat-banner" role="status">
                <p>VAT was charged and no supplier VAT number was on the issuer block. Input tax is on HMRC discretion (reg 29(2)). Type the number if it is printed — do not guess.</p>
              </div>`
            : ''
        }
        <p class="hint">${
          extracting
            ? 'Suggestions appear when extraction finishes. Everything stays editable after that.'
            : item.status === 'confirmed'
              ? 'Figures are confirmed. Save if you need to correct one, then Send to Xero below.'
              : item.status === 'sent_to_accounts'
                ? 'Locked after send to Xero. The draft bill keeps the account it was created with.'
                : 'Suggestions from the document — edit anything that looks wrong, then Confirm.'
        }</p>
        <form id="receipt-form" novalidate>
          <label class="field${fieldLow(item, 'supplier_name') ? ' is-low-confidence' : ''}${blockedFieldNames.includes('supplier_name') ? ' is-blocked' : ''}">${fieldLabelHtml('Supplier', item, 'supplier_name')}
            <input name="supplier_name" maxlength="500" value="${escapeHtml(item.supplier_name ?? '')}" ${canEdit ? '' : 'readonly'} />
          </label>
          <label class="field${fieldLow(item, 'receipt_date') ? ' is-low-confidence' : ''}${blockedFieldNames.includes('receipt_date') ? ' is-blocked' : ''}">${fieldLabelHtml('Date', item, 'receipt_date')}
            <input type="date" name="receipt_date" value="${dateInputValue(item.receipt_date)}" ${canEdit ? '' : 'readonly'} />
            ${dateUnsettledHtml(item, canEdit)}
          </label>
          <label class="field${fieldLow(item, 'currency') ? ' is-low-confidence' : ''}${!item.currency || blockedFieldNames.includes('currency') ? ' is-blocked' : ''}">${fieldLabelHtml('Currency', item, 'currency')}
            <select name="currency" ${canEdit ? '' : 'disabled'}>
              <option value="">Select…</option>
              ${currencyOptionsHtml(item.currency)}
            </select>
          </label>
          <label class="field${fieldLow(item, 'net_pence') ? ' is-low-confidence' : ''}">${fieldLabelHtml(`Net (${item.currency || 'as printed'})`, item, 'net_pence')}
            <input name="net" inputmode="decimal" value="${penceToInput(item.net_pence)}" ${canEdit ? '' : 'readonly'} />
          </label>
          <label class="field${fieldLow(item, 'vat_pence') ? ' is-low-confidence' : ''}">${fieldLabelHtml(`VAT (${item.currency || 'as printed'})`, item, 'vat_pence')}
            <input name="vat" inputmode="decimal" value="${penceToInput(item.vat_pence)}" ${canEdit ? '' : 'readonly'} />
          </label>
          <label class="field${fieldLow(item, 'gross_pence') ? ' is-low-confidence' : ''}${blockedFieldNames.includes('gross') ? ' is-blocked' : ''}">${fieldLabelHtml(`Gross (${item.currency || 'as printed'})`, item, 'gross_pence')}
            <input name="gross" inputmode="decimal" required value="${penceToInput(item.gross_pence)}" ${canEdit ? '' : 'readonly'} />
          </label>
          ${gbpPreviewHtml(item)}
          <label class="field${fieldLow(item, 'supplier_vat_number') ? ' is-low-confidence' : ''}">${fieldLabelHtml('Supplier VAT number', item, 'supplier_vat_number')}
            <input name="supplier_vat_number" maxlength="32" autocomplete="off" value="${escapeHtml(item.supplier_vat_number ?? '')}" ${canEdit ? '' : 'readonly'} />
          </label>
          <label class="field${fieldLow(item, 'category') ? ' is-low-confidence' : ''}${blockedFieldNames.includes('category') ? ' is-blocked' : ''}">${fieldLabelHtml('Category', item, 'category')}
            <select name="category" ${canEdit ? '' : 'disabled'}>
              <option value="">Select…</option>
              ${categoryOptions
                .map(
                  (c) =>
                    `<option value="${escapeHtml(c.id)}" ${item.category === c.id ? 'selected' : ''}>${escapeHtml(
                      c.vat_recovery_blocked
                        ? `${c.label} (VAT not recoverable)`
                        : c.label,
                    )}</option>`,
                )
                .join('')}
            </select>
          </label>
          ${taxHintHtml(item, categoryOptions)}
          <label class="field"><span>Notes</span>
            <textarea name="notes" maxlength="5000" rows="2" ${canEdit ? '' : 'readonly'}>${escapeHtml(item.notes ?? '')}</textarea>
          </label>
          ${
            canEdit
              ? `<button class="submit" type="submit" data-save>Save</button>
                 ${
                   item.status === 'confirmed'
                     ? ''
                     : `<button class="submit" type="button" data-confirm style="margin-top:8px">Confirm</button>
                        <p class="hint">Confirm locks the figures. It does not send to Xero yet.</p>`
                 }
                 <button type="button" class="btn-secondary btn-block" data-delete style="margin-top:10px">Delete</button>`
              : extracting
                ? ''
                : ''
          }
        </form>
        ${manualResolveHtml(item)}
        ${
          item.syncState === 'synced'
            ? `<button type="button" class="btn-secondary btn-block" data-archive>${
                item.archived_at ? 'Unarchive' : 'Archive'
              }</button>
               <p class="hint">Archive only hides this row. A hold, a failed push, and an unrecharged cost still count.</p>`
            : ''
        }
        ${rechargeSectionHtml(item, rechargeLists.clients, rechargeLists.sites)}
        ${supplierContactSectionHtml(item, contactPreview)}
        ${receiptHoldSectionHtml(item, 'idle')}
        ${receiptBillReadbackHtml(item)}
        ${xeroPushSectionHtml(item, accpayPreview, xeroPushPhase)}
      </section>
    `;

    root.querySelector('[data-back]')?.addEventListener('click', () => {
      clearExtractPoll();
      void renderList();
    });

    if (extracting && item.syncState === 'synced') {
      extractPollTimer = setTimeout(() => {
        void (async () => {
          if (view !== 'detail' || detailId !== id) return;
          try {
            const remote = await fetchReceipt(id);
            const cur = await getReceiptLocal(id);
            if (!cur) return;
            await putReceiptLocal({
              ...cur,
              status: remote.status as LocalReceipt['status'],
              supplier_name: remote.supplier_name,
              receipt_date: remote.receipt_date,
              currency: remote.currency ?? null,
              net_pence: remote.net_pence,
              vat_pence: remote.vat_pence,
              gross_pence: remote.gross_pence,
              gbp_net_pence: remote.gbp_net_pence ?? null,
              gbp_vat_pence: remote.gbp_vat_pence ?? null,
              gbp_gross_pence: remote.gbp_gross_pence ?? null,
              fx_rate: remote.fx_rate ?? null,
              fx_rate_as_of: remote.fx_rate_as_of ?? null,
              fx_source: remote.fx_source ?? null,
              fx_period: remote.fx_period ?? null,
              category: remote.category,
              ...vatFieldsFromRemote(remote),
              notes: remote.notes,
              has_thumb: Boolean(remote.has_thumb),
              extraction_error: remote.extraction_error ?? null,
              extraction_cost_usd: remote.extraction_cost_usd ?? null,
              money_reconcile_ok: remote.money_reconcile_ok ?? null,
              money_reconcile_note: remote.money_reconcile_note ?? null,
              money_reconcile_overridden:
                remote.money_reconcile_overridden ?? false,
              low_confidence_fields: remote.low_confidence_fields ?? [],
              fields_edited: remote.fields_edited ?? [],
              confidence: remote.extraction_suggested?.confidence ?? null,
              rechargeable: remote.rechargeable ?? cur.rechargeable ?? null,
              client_id: remote.client_id ?? cur.client_id ?? null,
              site_id: remote.site_id ?? cur.site_id ?? null,
              recharge_job_id: remote.recharge_job_id ?? cur.recharge_job_id ?? null,
              recharge_frozen: remote.recharge_frozen ?? cur.recharge_frozen ?? false,
              xero_bill_id: remote.xero_bill_id ?? cur.xero_bill_id ?? null,
              xero_attachment_status:
                remote.xero_attachment_status ?? cur.xero_attachment_status ?? null,
              xero_push_error: remote.xero_push_error ?? cur.xero_push_error ?? null,
              xero_push_outcome:
                remote.xero_push_outcome ?? cur.xero_push_outcome ?? null,
              updatedAt: new Date().toISOString(),
            });
          } catch (err) {
            console.warn('receipt extraction poll failed', err);
          }
          if (view === 'detail' && detailId === id) {
            await renderDetail(id);
          }
        })();
      }, 1500);
    }

    root.querySelector('[data-retry-upload]')?.addEventListener('click', () => {
      void (async () => {
        await requeueFailed(id);
        opts.onChange?.();
        await renderDetail(id);
      })();
    });

    async function onRetake(file: File | undefined) {
      if (!file || file.size === 0) {
        window.alert('Nothing was selected. Pick a PDF invoice or a photo.');
        return;
      }
      try {
        const prepared = await prepareReceiptUploadFile(file, {
          mimeType: file.type,
          filename: file.name,
        });
        await retakeReceiptLocal({
          id,
          blob: prepared,
          mime_type: prepared.type || 'image/jpeg',
        });
        opts.onChange?.();
        await renderDetail(id);
      } catch (err) {
        window.alert(
          err instanceof ReceiptTooLargeError ||
            err instanceof ReceiptUnsupportedTypeError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Couldn’t replace that file. Try a PDF or a smaller photo.',
        );
      }
    }
    root.querySelector('[data-retake]')?.addEventListener('change', (e) => {
      void onRetake((e.target as HTMLInputElement).files?.[0]);
    });
    root
      .querySelector('[data-retake-library]')
      ?.addEventListener('change', (e) => {
        void onRetake((e.target as HTMLInputElement).files?.[0]);
      });

    const form = root.querySelector('#receipt-form') as HTMLFormElement | null;

    async function readFields() {
      if (!form) {
        throw new Error('Receipt form is missing — go back and open it again.');
      }
      const fd = new FormData(form);
      return {
        supplier_name: String(fd.get('supplier_name') ?? '').trim() || null,
        receipt_date: String(fd.get('receipt_date') ?? '') || null,
        currency: String(fd.get('currency') ?? '').trim().toUpperCase() || null,
        net_pence: poundsToPence(String(fd.get('net') ?? '')),
        vat_pence: poundsToPence(String(fd.get('vat') ?? '')),
        gross_pence: poundsToPence(String(fd.get('gross') ?? '')),
        category: String(fd.get('category') ?? '') || null,
        supplier_vat_number:
          String(fd.get('supplier_vat_number') ?? '').trim() || null,
        notes: String(fd.get('notes') ?? '').trim() || null,
      };
    }

    function setBusy(busy: boolean) {
      form
        ?.querySelectorAll('button')
        .forEach((btn) => {
          (btn as HTMLButtonElement).disabled = busy;
        });
    }

    function paintLiveFeedback(
      kind: 'ok' | 'error',
      text: string,
      lines: string[] = [],
    ) {
      let box = root.querySelector('[data-receipt-feedback]') as HTMLElement | null;
      if (!box) {
        box = document.createElement('div');
        box.setAttribute('data-receipt-feedback', '');
        box.setAttribute('role', 'alert');
        form?.insertAdjacentElement('beforebegin', box);
      }
      box.className =
        kind === 'error' ? 'receipt-fail-banner' : 'receipt-ok-banner';
      box.innerHTML = `<p>${escapeHtml(text)}</p>${
        lines.length
          ? `<ul class="receipt-block-list">${lines
              .map((l) => `<li>${escapeHtml(l)}</li>`)
              .join('')}</ul>`
          : ''
      }`;
      form?.querySelectorAll('.field.is-blocked').forEach((el) => {
        el.classList.remove('is-blocked');
      });
      for (const name of blockedFieldNames) {
        form
          ?.querySelector(`[name="${name}"]`)
          ?.closest('.field')
          ?.classList.add('is-blocked');
      }
      box.scrollIntoView({ block: 'nearest' });
    }

    bindRechargeSection(root, {
      item,
      clients: rechargeLists.clients,
      sites: rechargeLists.sites,
      onChange: async () => {
        opts.onChange?.();
        await renderDetail(id);
      },
      onOpenJob: opts.onOpenJob,
      onError: (text) => {
        paintLiveFeedback('error', text);
      },
    });

    bindSupplierContactSection(root, {
      item,
      onChange: async () => {
        opts.onChange?.();
        await renderDetail(id);
      },
      onError: (text) => {
        paintLiveFeedback('error', text);
      },
    });

    bindReceiptHoldSection(root, {
      item,
      onChange: async () => {
        opts.onChange?.();
        await renderDetail(id);
      },
      onError: (text) => {
        paintLiveFeedback('error', text);
      },
    });

    bindXeroPushSection(root, {
      item,
      preview: accpayPreview,
      phase: xeroPushPhase,
      setPhase: (phase) => {
        xeroPushPhase = phase;
        void renderDetail(id);
      },
      onChange: async () => {
        opts.onChange?.();
        await renderDetail(id);
      },
      onError: (text) => {
        detailFeedback = { kind: 'error', text };
      },
    });

    async function showFeedbackAndRerender() {
      opts.onChange?.();
      await renderDetail(id);
    }

    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      await saveFields(false);
    });
    root.querySelector('[data-confirm]')?.addEventListener('click', () => {
      void saveFields(true);
    });
    root
      .querySelector('[data-money-override]')
      ?.addEventListener('change', (e) => {
        moneyOverrideAck = (e.target as HTMLInputElement).checked;
      });
    root.querySelectorAll('[data-money-fix]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const el = btn as HTMLButtonElement;
        const netEl = form?.querySelector('[name="net"]') as HTMLInputElement | null;
        const vatEl = form?.querySelector('[name="vat"]') as HTMLInputElement | null;
        const grossEl = form?.querySelector('[name="gross"]') as HTMLInputElement | null;
        if (netEl) netEl.value = penceToInput(Number(el.dataset.net));
        if (vatEl) vatEl.value = penceToInput(Number(el.dataset.vat));
        if (grossEl) grossEl.value = penceToInput(Number(el.dataset.gross));
        moneyOverrideAck = false;
        void saveFields(false);
      });
    });
    bindManualResolve(
      root,
      id,
      async () => {
        opts.onChange?.();
        await renderDetail(id);
      },
      (text) => {
        detailFeedback = { kind: 'error', text };
      },
    );
    root.querySelector('[data-archive]')?.addEventListener('click', () => {
      void (async () => {
        const cur = await getReceiptLocal(id);
        if (!cur) return;
        const next = !cur.archived_at;
        try {
          const remote = await setReceiptArchivedRemote(id, next);
          await putReceiptLocal({
            ...cur,
            archived_at: remote.archived_at ?? null,
            updatedAt: new Date().toISOString(),
          });
          detailFeedback = {
            kind: 'ok',
            text: next
              ? 'Archived. It still counts if it needs you.'
              : 'Back on the current list.',
          };
          await renderDetail(id);
          opts.onChange?.();
        } catch (err) {
          detailFeedback = {
            kind: 'error',
            text:
              err instanceof Error
                ? err.message
                : 'Couldn’t change the archive.',
          };
          await renderDetail(id);
        }
      })();
    });
    root.querySelector('[data-delete]')?.addEventListener('click', async () => {
      if (!window.confirm('Delete this receipt?')) return;
      const cur = await getReceiptLocal(id);
      if (!cur) {
        detailFeedback = {
          kind: 'error',
          text: 'Receipt isn’t on this device. Go back to the list and open it again.',
        };
        await renderDetail(id);
        return;
      }
      if (cur.status === 'pending_upload' || cur.blob) {
        await deleteReceiptLocal(id);
      } else {
        try {
          await deleteReceiptRemote(id);
        } catch (err) {
          detailFeedback = {
            kind: 'error',
            text:
              err instanceof Error
                ? `Couldn’t delete: ${err.message}`
                : 'Couldn’t delete this receipt on the server.',
          };
          await renderDetail(id);
          return;
        }
        await deleteReceiptLocal(id);
      }
      opts.onChange?.();
      await renderList();
    });

    async function saveFields(confirm: boolean) {
      try {
        const fields = await readFields();
        if (confirm) {
          const blockers = confirmBlockers({
            ...fields,
            money_reconcile_overridden: moneyOverrideAck,
          });
          if (blockers.length) {
            blockedFieldNames = [];
            if (!fields.supplier_name) blockedFieldNames.push('supplier_name');
            if (!fields.receipt_date) blockedFieldNames.push('receipt_date');
            if (!fields.category) blockedFieldNames.push('category');
            if (!fields.currency) blockedFieldNames.push('currency');
            if (fields.gross_pence == null) blockedFieldNames.push('gross');
            paintLiveFeedback('error', 'Cannot confirm yet.', blockers);
            return;
          }
          blockedFieldNames = [];
        }
        const cur = await getReceiptLocal(id);
        if (!cur) {
          detailFeedback = {
            kind: 'error',
            text: 'Receipt isn’t on this device. Go back to the list and open it again.',
          };
          await renderDetail(id);
          return;
        }

        if (cur.status === 'confirmed' && confirm) {
          detailFeedback = {
            kind: 'ok',
            text: 'Already confirmed.',
          };
          await renderDetail(id);
          return;
        }

        const dateAccept = Boolean(
          (root.querySelector('[data-date-accept]') as HTMLInputElement | null)
            ?.checked,
        );
        const dateEdited =
          Boolean(fields.receipt_date) &&
          fields.receipt_date !== (cur.receipt_date ?? '').slice(0, 10);
        if (confirm) {
          const dateDecision = receiptDateDecision(
            cur,
            fields.receipt_date,
            dateEdited,
            dateAccept,
          );
          if (dateDecision && !dateDecision.ok) {
            paintLiveFeedback('error', 'Cannot confirm yet.', [
              dateDecision.message,
            ]);
            return;
          }
        }

        setBusy(true);

        if (cur.blob || cur.status === 'pending_upload') {
          const localMoney = reconcileMoney(fields);
          const overridden = confirm && !localMoney.ok ? moneyOverrideAck : false;
          await putReceiptLocal({
            ...cur,
            ...fields,
            vat_evidence_state: localVatEvidence(cur, fields),
            status: confirm ? 'confirmed' : cur.status,
            money_reconcile_ok: localMoney.ok,
            money_reconcile_note: localMoney.note,
            money_reconcile_overridden: overridden,
            accept_receipt_date: confirm ? dateAccept : cur.accept_receipt_date,
            xero_push_outcome: confirm
              ? overridden
                ? 'blocked'
                : 'queued'
              : cur.xero_push_outcome,
            xero_push_error: confirm
              ? overridden
                ? MONEY_OVERRIDE_BLOCK
                : null
              : cur.xero_push_error,
            updatedAt: new Date().toISOString(),
          });
          blockedFieldNames = [];
          detailFeedback = {
            kind: 'ok',
            text: confirm
              ? overridden
                ? 'Confirmed. Needs you before Xero — money mismatch was overridden.'
                : 'Confirmed. Queued for Xero — will send once the photo uploads and you’re connected.'
              : 'Saved on this device — uploads when online.',
          };
          await showFeedbackAndRerender();
          return;
        }

        const remote = await patchReceiptRemote(id, {
          ...fields,
          ...(confirm ? { status: 'confirmed' } : {}),
          ...(confirm && moneyOverrideAck
            ? { money_reconcile_overridden: true }
            : {}),
          ...(confirm && dateAccept ? { accept_receipt_date: true } : {}),
        });
        await putReceiptLocal({
          ...cur,
          supplier_name: remote.supplier_name,
          receipt_date: remote.receipt_date,
          currency: remote.currency ?? fields.currency ?? cur.currency ?? null,
          net_pence: remote.net_pence,
          vat_pence: remote.vat_pence,
          gross_pence: remote.gross_pence,
          gbp_net_pence: remote.gbp_net_pence ?? null,
          gbp_vat_pence: remote.gbp_vat_pence ?? null,
          gbp_gross_pence: remote.gbp_gross_pence ?? null,
          fx_rate: remote.fx_rate ?? null,
          fx_rate_as_of: remote.fx_rate_as_of ?? null,
          fx_source: remote.fx_source ?? null,
          fx_period: remote.fx_period ?? null,
          category: remote.category,
          ...vatFieldsFromRemote(remote),
          notes: remote.notes,
          status: remote.status as LocalReceipt['status'],
          syncState: 'synced',
          money_reconcile_ok: remote.money_reconcile_ok ?? null,
          money_reconcile_note: remote.money_reconcile_note ?? null,
          money_reconcile_overridden:
            remote.money_reconcile_overridden ?? false,
          rechargeable: remote.rechargeable ?? cur.rechargeable ?? null,
          client_id: remote.client_id ?? cur.client_id ?? null,
          site_id: remote.site_id ?? cur.site_id ?? null,
          recharge_job_id: remote.recharge_job_id ?? null,
          recharge_frozen: remote.recharge_frozen ?? false,
          xero_bill_id: remote.xero_bill_id ?? cur.xero_bill_id ?? null,
          xero_attachment_status:
            remote.xero_attachment_status ?? cur.xero_attachment_status ?? null,
          xero_push_error: remote.xero_push_error ?? cur.xero_push_error ?? null,
          xero_push_outcome:
            remote.xero_push_outcome ?? cur.xero_push_outcome ?? null,
          updatedAt: new Date().toISOString(),
        });
        blockedFieldNames = [];
        const display = receiptPushDisplayState({
          status: remote.status,
          xero_bill_id: remote.xero_bill_id,
          xero_push_outcome: remote.xero_push_outcome,
          held: remote.held,
          money_reconcile_overridden: remote.money_reconcile_overridden,
        });
        detailFeedback = {
          kind: 'ok',
          text: confirm
            ? display === 'sent_to_accounts'
              ? 'Confirmed and sent to Xero.'
              : display === 'queued'
                ? 'Confirmed. Queued for Xero — will send when you’re connected.'
                : display === 'action_needed'
                  ? `Confirmed. Needs you before Xero — ${remote.xero_push_error ?? 'see the reason on this receipt'}.`
                  : 'Confirmed.'
            : 'Saved.',
        };
        await showFeedbackAndRerender();
      } catch (err) {
        console.error('receipt save failed', err);
        setBusy(false);
        paintLiveFeedback(
          'error',
          err instanceof Error ? err.message : 'Could not save receipt',
        );
      }
    }
  }

  subscribeWideLayout(() => {
    syncSplitClasses();
    void refresh();
  });

  await renderList();
  return {
    refresh,
    openCapture,
    open: async (id: string) => {
      view = 'detail';
      detailId = id;
      syncSplitClasses();
      markSelectedRow();
      await renderDetail(id);
    },
  };
}
